// ── RemoteSnapshotBrowser — Browse remote env with delta annotations ───────
//
// Two-column layout mirroring SourceScopesPanel:
//   Left  → namespace sidebar with delta badges (new/synced/changed)
//   Right → scope keys with delta indicators + selection checkboxes
//
// Delta colors:
//   🟢 new     — emerald  (exists on remote, not locally)
//   🔵 synced  — sky/blue (exists on both)
//   🟡 changed — amber    (exists on both, count differs)
//   ⚪ local_only — neutral (exists locally only)
//
import React from 'react';
import {
  Check,
  Minus,
  Search,
  Camera,
  RefreshCw,
  GitCompareArrows,
  Loader2,
  Plus,
  ArrowDownToLine,
  ShieldAlert,
  WifiOff,
  Clock,
  ServerCrash,
  KeyRound,
  Activity,
  Stethoscope,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import type {
  SnapshotNamespace,
  SnapshotScope,
  SnapshotNamespaceSummary,
  SnapshotScopeDelta,
  RemoteHealthResult,
} from '../../../api/snapshotApi';
import type { SnapshotPhase } from '../hooks/useRemoteSnapshot';

// ── Delta badge colors ───────────────────────────────────────────────────

const DELTA_COLORS = {
  new: { bg: 'bg-emerald-900/50', text: 'text-emerald-400', border: 'border-emerald-700/50', label: 'NEW' },
  synced: { bg: 'bg-sky-900/30', text: 'text-sky-400', border: 'border-sky-700/40', label: 'SYNCED' },
  changed: { bg: 'bg-amber-900/40', text: 'text-amber-400', border: 'border-amber-700/40', label: 'CHANGED' },
  local_only: { bg: 'bg-neutral-800/50', text: 'text-neutral-500', border: 'border-neutral-700/40', label: 'LOCAL' },
} as const;

function DeltaBadge({ delta }: { delta: keyof typeof DELTA_COLORS }) {
  const c = DELTA_COLORS[delta];
  return (
    <span className={`text-[8px] font-semibold px-1 py-0.5 rounded ${c.bg} ${c.text} border ${c.border} uppercase tracking-wider`}>
      {c.label}
    </span>
  );
}

// ── Error code → UX mapping ──────────────────────────────────────────────

const SNAPSHOT_ERROR_UX: Record<string, { icon: typeof ShieldAlert; title: string; color: string; hint: string }> = {
  remote_auth_failed: {
    icon: ShieldAlert,
    title: 'Authentication Failed',
    color: 'text-amber-400',
    hint: '', // derived from diagnostics — see deriveAuthHint()
  },
  remote_not_found: {
    icon: ServerCrash,
    title: 'Endpoint Not Found',
    color: 'text-rose-400',
    hint: 'The remote BFF may be running a different version. Verify both environments are up to date.',
  },
  remote_server_error: {
    icon: ServerCrash,
    title: 'Remote Server Error',
    color: 'text-rose-400',
    hint: 'The remote environment encountered an internal error. Check its logs and try again.',
  },
  remote_timeout: {
    icon: Clock,
    title: 'Connection Timed Out',
    color: 'text-amber-400',
    hint: 'The remote environment took too long to respond. Check that it\u2019s running and reachable.',
  },
  remote_unreachable: {
    icon: WifiOff,
    title: 'Remote Unreachable',
    color: 'text-rose-400',
    hint: 'Could not connect to the remote environment. Verify the URL and network connectivity.',
  },
  no_auth: {
    icon: ShieldAlert,
    title: 'No Credentials',
    color: 'text-amber-400',
    hint: 'No authentication method is available. Pair using Device Auth or configure Service Auth.',
  },
};

const DEFAULT_ERROR_UX = {
  icon: ServerCrash,
  title: 'Snapshot Failed',
  color: 'text-rose-400',
  hint: 'An unexpected error occurred. Try again or check the remote environment.',
};

/**
 * Derive a context-aware hint for auth errors using diagnostic results.
 * Reads the `cause` field injected by the backend health check to
 * distinguish secret mismatch (service JWT) from token expiry (device auth).
 */
function deriveAuthHint(diagnostics: RemoteHealthResult | null): string {
  if (!diagnostics) {
    return 'Authentication with the remote environment failed. Run diagnostics for details, or click Reconnect to re-pair.';
  }
  const readCheck = diagnostics.checks.find(c => c.step === 'authenticated_read');
  if (readCheck?.remediation) return readCheck.remediation;

  const authCheck = diagnostics.checks.find(c => c.step === 'auth');
  if (authCheck?.method === 'service_jwt') {
    return 'Service JWT was accepted locally but rejected by the remote — the shared secret (MIGRATE_SERVICE_SECRET or APP_JWT_SECRET) likely differs between environments. Set a matching value on both, or use Device Auth pairing instead.';
  }
  return 'Your session with the remote environment has expired or is invalid. Disconnect and re-pair, or enable Service Auth for zero-interaction access.';
}

// ── Props ────────────────────────────────────────────────────────────────

interface RemoteSnapshotBrowserProps {
  // Phase & status
  phase: SnapshotPhase;
  error: string | null;
  errorCode: string | null;
  snapshotId: string | null;
  capturedAt: string | null;

  /** Structured remote connectivity diagnostics (auto-run on failure). */
  diagnostics: RemoteHealthResult | null;
  /** True while diagnostics probe is running. */
  diagnosing: boolean;
  /** Trigger a manual diagnostic run. */
  onRunDiagnostics: () => void;

  /** Label for the remote source environment (e.g. "Docker Dev"). */
  sourceEnvLabel: string;

  // Namespace data
  namespaces: SnapshotNamespace[];
  localOnlyNamespaces: SnapshotNamespace[];
  namespaceSummary: SnapshotNamespaceSummary | null;

  // Namespace browsing
  selectedNs: string | null;
  onSelectNs: (ns: string | null) => void;
  nsSearch: string;
  onNsSearchChange: (v: string) => void;
  filteredNamespaces: SnapshotNamespace[];

  // Scope keys
  scopeKeys: SnapshotScope[];
  localOnlyScopes: SnapshotScope[];
  scopeDelta: SnapshotScopeDelta | null;
  scopesLoading: boolean;
  keySearch: string;
  onKeySearchChange: (v: string) => void;
  filteredScopes: SnapshotScope[];

  // Selection
  selectedScopeIds: Set<number>;
  onToggleScopeId: (id: number) => void;
  onToggleAllScopes: () => void;
  onSelectAllNew: () => void;
  allFilteredSelected: boolean;
  someFilteredSelected: boolean;

  // Derived counts
  selectedNewCount: number;
  selectedSyncedCount: number;
  totalSelectedCount: number;

  // Actions
  onCaptureSnapshot: () => void;
  onClearSnapshot: () => void;

  // Auth reconnect — triggered for auth errors to re-pair
  onReconnect?: () => void;

  // Pull integration
  onPullSelected: () => void;
  canPull: boolean;
}

export function RemoteSnapshotBrowser({
  phase,
  error,
  errorCode,
  snapshotId,
  capturedAt,
  namespaces,
  localOnlyNamespaces,
  namespaceSummary,
  selectedNs,
  onSelectNs,
  nsSearch,
  onNsSearchChange,
  filteredNamespaces,
  scopeKeys,
  localOnlyScopes,
  scopeDelta,
  scopesLoading,
  keySearch,
  onKeySearchChange,
  filteredScopes,
  selectedScopeIds,
  onToggleScopeId,
  onToggleAllScopes,
  onSelectAllNew,
  allFilteredSelected,
  someFilteredSelected,
  selectedNewCount,
  selectedSyncedCount,
  totalSelectedCount,
  onCaptureSnapshot,
  onClearSnapshot,
  onReconnect,
  onPullSelected,
  canPull,
  sourceEnvLabel,
  diagnostics,
  diagnosing,
  onRunDiagnostics,
}: RemoteSnapshotBrowserProps) {
  const isAuthError = errorCode === 'remote_auth_failed' || errorCode === 'no_auth';
  // ── Idle / Loading namespaces ────────────────────────────────────────
  if (phase === 'idle' || phase === 'loading_namespaces') {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-8 px-4">
        <GitCompareArrows className="w-8 h-8 text-sky-500/60" />
        <p className="text-xs text-neutral-400 text-center max-w-[260px]">
          Take a snapshot to browse what&apos;s available on the remote environment and compare with your local data.
        </p>
        <button
          onClick={onCaptureSnapshot}
          disabled={phase === 'loading_namespaces'}
          className={
            'px-4 py-2 rounded border text-xs flex items-center gap-2 transition-colors ' +
            (phase === 'loading_namespaces'
              ? 'border-neutral-700 text-neutral-500 cursor-wait'
              : 'border-sky-700 text-sky-200 hover:border-sky-500 hover:bg-sky-900/20')
          }
        >
          {phase === 'loading_namespaces' ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Taking snapshot…</>
          ) : (
            <><Camera className="w-3.5 h-3.5" /> Take Snapshot</>
          )}
        </button>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (phase === 'error') {
    const ux = (errorCode && SNAPSHOT_ERROR_UX[errorCode]) || DEFAULT_ERROR_UX;
    const Icon = ux.icon;
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-6 px-4">
        <div className="flex items-center gap-2">
          <Icon className={`w-5 h-5 ${ux.color}`} />
          <span className={`text-sm font-medium ${ux.color}`}>{ux.title}</span>
        </div>
        <p className="text-[11px] text-neutral-400 text-center max-w-[300px] leading-relaxed">
          {errorCode === 'remote_auth_failed' ? deriveAuthHint(diagnostics ?? null) : ux.hint}
        </p>

        {/* ── Diagnostic checklist ─────────────────────────────────── */}
        {diagnosing && (
          <div className="flex items-center gap-2 text-[10px] text-neutral-500 mt-1">
            <Loader2 className="w-3 h-3 animate-spin" /> Running diagnostics…
          </div>
        )}
        {diagnostics && !diagnosing && (
          <div className="w-full max-w-[340px] mt-2 rounded border border-neutral-700/60 bg-neutral-900/40 p-2.5 space-y-1.5">
            <div className="flex items-center gap-1.5 text-[10px] font-medium text-neutral-300 mb-1.5">
              <Stethoscope className="w-3 h-3 text-neutral-500" />
              Connectivity Diagnostics
            </div>
            {diagnostics.checks.map((check, i) => {
              const StepIcon = check.ok ? CheckCircle2 : XCircle;
              const stepColor = check.ok ? 'text-emerald-400' : 'text-rose-400';
              const stepLabels: Record<string, string> = {
                reachable: 'Remote Reachable',
                env_info: 'Environment Info',
                auth: 'Authentication',
                authenticated_read: 'Authenticated Read',
              };
              return (
                <div key={i} className="flex items-start gap-2">
                  <StepIcon className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${stepColor}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] font-medium text-neutral-300">
                        {stepLabels[check.step] ?? check.step}
                      </span>
                      {check.latency_ms != null && (
                        <span className="text-[9px] text-neutral-500">{check.latency_ms}ms</span>
                      )}
                    </div>
                    <p className="text-[10px] text-neutral-500 leading-snug break-words">
                      {check.detail}
                    </p>
                    {!check.ok && check.remediation && (
                      <p className="mt-0.5 text-[10px] text-amber-400/90 leading-snug break-words">
                        ⚑ {check.remediation}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mt-1 justify-center">
          {isAuthError && onReconnect && (
            <button
              onClick={onReconnect}
              className="px-3 py-1.5 rounded border border-amber-700 text-xs text-amber-200 hover:border-amber-500 hover:bg-amber-950/20 flex items-center gap-1.5 transition-colors"
            >
              <KeyRound className="w-3 h-3" /> Reconnect
            </button>
          )}
          <button
            onClick={onRunDiagnostics}
            disabled={diagnosing}
            className="px-3 py-1.5 rounded border border-neutral-600 text-xs text-neutral-300 hover:border-neutral-400 hover:bg-neutral-800/30 flex items-center gap-1.5 transition-colors disabled:opacity-40"
          >
            <Activity className="w-3 h-3" /> {diagnostics ? 'Re-check' : 'Diagnose'}
          </button>
          <button
            onClick={onCaptureSnapshot}
            className="px-3 py-1.5 rounded border border-sky-700 text-xs text-sky-200 hover:border-sky-500 hover:bg-sky-950/20 flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw className="w-3 h-3" /> Retry
          </button>
          <button
            onClick={onClearSnapshot}
            className="px-3 py-1.5 rounded border border-neutral-700 text-xs text-neutral-400 hover:border-neutral-500 transition-colors"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  // ── Ready — show delta browser ───────────────────────────────────────

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* ── Header ──────────────────────────────────────── */}
      <div className="px-3 py-2 border-b border-neutral-800 pac-glass-subtle flex items-center justify-between shrink-0">
        <div className="flex items-center gap-2">
          <Camera className="w-3.5 h-3.5 text-sky-400" />
          <span className="text-sm font-medium text-neutral-200">Remote</span>
          <span className="text-[10px] text-cyan-400 font-medium">{sourceEnvLabel}</span>
          {capturedAt && (
            <span className="text-[9px] text-neutral-500">
              {new Date(capturedAt).toLocaleTimeString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {namespaceSummary && (
            <div className="flex items-center gap-1.5 text-[10px]">
              {namespaceSummary.new_count > 0 && (
                <span className="text-emerald-400">{namespaceSummary.new_count} new</span>
              )}
              {namespaceSummary.changed_count > 0 && (
                <span className="text-amber-400">{namespaceSummary.changed_count} changed</span>
              )}
              {namespaceSummary.synced_count > 0 && (
                <span className="text-sky-400">{namespaceSummary.synced_count} synced</span>
              )}
            </div>
          )}
          <button
            onClick={onCaptureSnapshot}
            className="text-[10px] text-neutral-500 hover:text-sky-400 transition-colors"
            title="Refresh snapshot"
          >
            <RefreshCw className="w-3 h-3" />
          </button>
          <button
            onClick={onClearSnapshot}
            className="text-[10px] text-neutral-500 hover:text-rose-400 transition-colors"
            title="Clear snapshot"
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Grid: namespaces | scope keys ─────────────── */}
      <div className="flex-1 min-h-0 grid grid-cols-[200px_1fr] overflow-hidden">
        {/* ── Namespace sidebar ── */}
        <div className="min-h-0 flex flex-col border-r border-neutral-800/50 overflow-hidden">
          <div className="px-2 py-1.5 border-b border-neutral-800/50 shrink-0">
            <div className="relative">
              <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-600" />
              <input
                type="text"
                value={nsSearch}
                onChange={(e) => onNsSearchChange(e.target.value)}
                placeholder="Filter namespaces…"
                className="w-full pl-6 pr-2 py-1 rounded bg-neutral-900/50 border border-neutral-800 text-[11px] text-neutral-300 placeholder:text-neutral-600 focus:border-neutral-600 outline-none"
              />
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {filteredNamespaces.length === 0 ? (
              <div className="p-3 text-[11px] text-neutral-500 text-center">
                {nsSearch ? 'No matching namespaces.' : 'No namespaces found.'}
              </div>
            ) : (
              filteredNamespaces.map((ns) => {
                const isActive = selectedNs === ns.scope_namespace;

                return (
                  <button
                    key={ns.scope_namespace}
                    type="button"
                    onClick={() => onSelectNs(ns.scope_namespace)}
                    className={
                      'w-full text-left px-2 py-2 text-[11px] border-b border-neutral-800/30 ' +
                      'flex items-center gap-1.5 transition-colors cursor-default group ' +
                      (isActive
                        ? 'bg-neutral-800/80 text-white border-l-2 border-l-sky-500'
                        : 'text-neutral-300 hover:bg-neutral-900/50')
                    }
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-[10px]" title={ns.scope_namespace}>
                        {ns.scope_namespace}
                      </div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <DeltaBadge delta={ns.delta} />
                        <span className="text-[9px] text-neutral-500 tabular-nums">
                          {ns.count} remote
                          {ns.delta === 'changed' && ns.local_count != null && (
                            <span className="text-neutral-600"> · {ns.local_count} local</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}

            {/* Local-only namespaces (faded, non-selectable) */}
            {localOnlyNamespaces.length > 0 && (
              <>
                <div className="px-2 py-1 text-[9px] text-neutral-600 uppercase tracking-wider border-b border-neutral-800/30 bg-neutral-900/30">
                  Local only ({localOnlyNamespaces.length})
                </div>
                {localOnlyNamespaces.map((ns) => (
                  <div
                    key={ns.scope_namespace}
                    className="w-full text-left px-2 py-1.5 text-[10px] text-neutral-600 border-b border-neutral-800/20 font-mono truncate"
                    title={`${ns.scope_namespace} — exists locally but not on remote`}
                  >
                    {ns.scope_namespace}
                    <span className="ml-1 text-[9px]">({ns.count})</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* ── Scope keys ── */}
        <div className="min-h-0 flex flex-col overflow-hidden">
          {selectedNs && (
            <div className="px-2 py-1.5 border-b border-neutral-800/50 flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={onToggleAllScopes}
                className="shrink-0 group"
                title={allFilteredSelected ? 'Deselect all' : 'Select all'}
              >
                {allFilteredSelected ? (
                  <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-emerald-600 border border-emerald-500 group-hover:bg-emerald-500 transition-all">
                    <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                ) : someFilteredSelected ? (
                  <span className="w-3.5 h-3.5 rounded-[3px] flex items-center justify-center bg-amber-600/80 border border-amber-500 group-hover:bg-amber-500 transition-all">
                    <Minus className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                  </span>
                ) : (
                  <span className="w-3.5 h-3.5 rounded-[3px] inline-block border border-neutral-600 group-hover:border-neutral-400 transition-colors" />
                )}
              </button>
              <div className="relative flex-1 min-w-0">
                <Search className="absolute left-1.5 top-1/2 -translate-y-1/2 w-3 h-3 text-neutral-600" />
                <input
                  type="text"
                  value={keySearch}
                  onChange={(e) => onKeySearchChange(e.target.value)}
                  placeholder="Filter keys…"
                  className="w-full pl-6 pr-2 py-0.5 rounded bg-neutral-900/50 border border-neutral-800 text-[11px] text-neutral-300 placeholder:text-neutral-600 focus:border-neutral-600 outline-none"
                />
              </div>
              {scopeDelta && (
                <div className="flex items-center gap-1 text-[9px] shrink-0">
                  {scopeDelta.newCount > 0 && (
                    <span className="text-emerald-400 tabular-nums">{scopeDelta.newCount} new</span>
                  )}
                  {scopeDelta.syncedCount > 0 && (
                    <span className="text-sky-400 tabular-nums">{scopeDelta.syncedCount} synced</span>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex-1 min-h-0 overflow-y-auto">
            {!selectedNs ? (
              <div className="h-full flex items-center justify-center">
                <span className="text-[11px] text-neutral-500">
                  Select a namespace to browse scope keys
                </span>
              </div>
            ) : scopesLoading ? (
              <div className="h-full flex items-center justify-center gap-2">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-sky-400" />
                <span className="text-[11px] text-neutral-400">Loading scope keys…</span>
              </div>
            ) : filteredScopes.length === 0 ? (
              <div className="h-full flex items-center justify-center">
                <span className="text-[11px] text-neutral-500">
                  {keySearch ? 'No matching keys.' : 'Empty namespace.'}
                </span>
              </div>
            ) : (
              filteredScopes.map((s, idx) => {
                const id = s.id_scope;
                const isSelected = id != null && selectedScopeIds.has(id);

                return (
                  <button
                    key={`${s.scope_namespace}::${s.scope_key}::${idx}`}
                    type="button"
                    onClick={() => id != null && onToggleScopeId(id)}
                    className={
                      'w-full text-left px-2 py-1.5 text-[11px] border-b border-neutral-800/30 ' +
                      'flex items-center gap-2 transition-colors cursor-default group ' +
                      (isSelected
                        ? s.delta === 'new'
                          ? 'bg-emerald-950/40 text-emerald-200 border-l-2 border-l-emerald-500'
                          : 'bg-sky-950/30 text-sky-200 border-l-2 border-l-sky-500'
                        : 'text-neutral-300 hover:bg-black')
                    }
                  >
                    <span className="shrink-0">
                      {isSelected ? (
                        <Check className={`w-3.5 h-3.5 ${s.delta === 'new' ? 'text-emerald-400' : 'text-sky-400'}`} />
                      ) : (
                        <span className="w-3.5 h-3.5 inline-block border border-neutral-600 rounded-[3px] group-hover:border-neutral-400 transition-colors" />
                      )}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono" title={s.scope_key}>
                        {s.scope_key}
                      </div>
                    </div>
                    <div className="shrink-0 flex items-center gap-1.5">
                      <DeltaBadge delta={s.delta} />
                      {id != null && s.delta !== 'new' && (
                        <span className="text-[8px] text-neutral-700 tabular-nums" title="Matched local scope ID">↔ {id}</span>
                      )}
                    </div>
                  </button>
                );
              })
            )}

            {/* Local-only scopes in this namespace */}
            {localOnlyScopes.length > 0 && (
              <>
                <div className="px-2 py-1 text-[9px] text-neutral-600 uppercase tracking-wider border-b border-neutral-800/30 bg-neutral-900/30">
                  Local only ({localOnlyScopes.length})
                </div>
                {localOnlyScopes.slice(0, 50).map((s, idx) => (
                  <div
                    key={`local::${s.scope_key}::${idx}`}
                    className="w-full text-left px-2 py-1 text-[10px] text-neutral-600 border-b border-neutral-800/20 font-mono truncate pl-8"
                    title={`${s.scope_key} — exists locally but not on remote`}
                  >
                    {s.scope_key}
                  </div>
                ))}
                {localOnlyScopes.length > 50 && (
                  <div className="px-2 py-1 text-[9px] text-neutral-600 text-center">
                    +{localOnlyScopes.length - 50} more
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── Action bar ─────────────────────────────────── */}
      <div className="px-3 py-2 border-t border-neutral-800 pac-glass flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-3 text-[10px]">
          {totalSelectedCount > 0 ? (
            <>
              <span className="text-neutral-300">
                {totalSelectedCount} selected
              </span>
              {selectedNewCount > 0 && (
                <span className="text-emerald-400">{selectedNewCount} new</span>
              )}
              {selectedSyncedCount > 0 && (
                <span className="text-sky-400">{selectedSyncedCount} synced</span>
              )}
            </>
          ) : (
            <span className="text-neutral-500">
              Select remote scopes to pull
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {namespaceSummary && namespaceSummary.new_count > 0 && (
            <button
              onClick={onSelectAllNew}
              className="px-2 py-1 rounded border border-emerald-800 text-[10px] text-emerald-300 hover:border-emerald-600 flex items-center gap-1"
              title="Select all new scopes across all browsed namespaces"
            >
              <Plus className="w-2.5 h-2.5" /> Select All New
            </button>
          )}
          <button
            onClick={onPullSelected}
            disabled={!canPull || totalSelectedCount === 0}
            className={
              'px-3 py-1.5 rounded border text-xs flex items-center gap-1.5 transition-colors ' +
              (canPull && totalSelectedCount > 0
                ? 'border-sky-700 text-sky-200 hover:border-sky-500 hover:bg-sky-900/20'
                : 'border-neutral-800 text-neutral-600 cursor-not-allowed')
            }
          >
            <ArrowDownToLine className="w-3 h-3" />
            Pull Selected ({totalSelectedCount})
          </button>
        </div>
      </div>
    </div>
  );
}
